/**
 * Bonuses, never punishments (D244). A market day doubles income for a short while when
 * claimed; a lucky find on the map pays a few minutes of income when tapped. Both are offered
 * only while the player is here, so being away never misses one; an offer left unclaimed simply
 * goes, at no cost. Good weather comes on a check-in (see `checkInIdle`). A visitor (D289) calls at
 * the gate the same way: tapped, it pays a little, as a lucky find does; missed, it simply goes.
 * D331: a tapped lucky find or visitor also pays Trimmings; a missed one pays none.
 *
 * Offers fall in fixed slots: slot k of a kind opens at k x every, plus a seeded share of the
 * jitter, after the world was made. So the screen can poll at any time and get the same answer,
 * and nothing about them has to be stored until one is claimed.
 */
import type { IdleContent } from '@hedgerow/data';
import {
  checkInIdle,
  idleIncome,
  idleLuckyFindCoins,
  normaliseIdle,
  type IdleResult,
} from './world.ts';
import { noteIdleCollection } from './collection.ts';
import { idleFillLineage } from './lineage.ts';
import {
  idleBonusOn,
  idleChance,
  idleOpenSlot,
  idleSeed,
  idleSlotOpens,
  LUCK_FIND,
  LUCK_MARKET,
  LUCK_VISITOR,
  LUCK_VISITOR_WHO,
} from './luck.ts';
import { idleClamp } from './rules.ts';
import type { IdleWorldState } from './state.ts';

/**
 * D330: which visitor calls, weighted by each kind's `chance` (default 1), so a rare visitor
 * can be given a low chance without changing what tapping it pays (D242). `r` is in [0, 1).
 */
function idleVisitorKind(
  kinds: Array<{ id: string; chance?: number | undefined }>,
  r: number,
): string {
  const total = kinds.reduce((a, k) => a + (k.chance ?? 1), 0);
  let x = r * total;
  for (const k of kinds) {
    x -= k.chance ?? 1;
    if (x < 0) return k.id;
  }
  return kinds[kinds.length - 1]!.id;
}
import { idleEarnTrimmings } from './trimmings.ts';

export interface IdleBonusOffer {
  /** Claim it with this id: `market_day:12`, `lucky_find:40`, `visitor:7`. */
  id: string;
  kind: 'market_day' | 'lucky_find' | 'visitor';
  /** D289: a visitor's id from `content.bonuses.visitors.kinds`: who has called. */
  visitor?: string;
  /** When the offer goes. */
  until: number;
  /** A market day: what it multiplies all income by, and for how long once claimed. */
  factor?: number;
  durationMs?: number;
  /** A lucky find or a visitor: the coins it pays if tapped now. */
  coins?: number;
}

/** What the player can claim at `now`: nothing unless they are here. */
export function idleBonusOffers(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleBonusOffer[] {
  const b = content.bonuses;
  // None between the festival and the new year (D276): nothing earns then.
  if (state.festivalPending || now - state.lastSeen > b.presentMs) return [];
  const claimed = state.claimed ?? [];
  const out: IdleBonusOffer[] = [];
  const m = b.marketDay;
  // D348: a kind the theme does not offer never opens.
  const market = idleBonusOn(content, 'market_day')
    ? idleOpenSlot(
        state,
        LUCK_MARKET,
        { every: m.everyMs, jitter: m.jitterMs, lasts: m.offerMs },
        now,
      )
    : undefined;
  if (market && !claimed.includes(`market_day:${market.k}`))
    out.push({
      id: `market_day:${market.k}`,
      kind: 'market_day',
      until: market.until,
      factor: m.factor,
      durationMs: m.durationMs,
    });
  const f = b.luckyFind;
  const find = idleBonusOn(content, 'lucky_find')
    ? idleOpenSlot(
        state,
        LUCK_FIND,
        { every: f.everyMs, jitter: f.jitterMs, lasts: f.lastsMs },
        now,
      )
    : undefined;
  if (find && !claimed.includes(`lucky_find:${find.k}`))
    out.push({
      id: `lucky_find:${find.k}`,
      kind: 'lucky_find',
      until: find.until,
      coins: idleLuckyFindCoins(state, content, now),
    });
  const v = idleVisitorAt(state, content, now);
  if (v && !claimed.includes(`visitor:${v.k}`))
    out.push({
      id: `visitor:${v.k}`,
      kind: 'visitor',
      until: v.until,
      visitor: v.visitor,
      coins: idleVisitorCoins(state, content, now),
    });
  return out;
}

/**
 * D289: the visitor at the gate at `now`, if one has called, whether or not the player is here:
 * its slot, when it goes, and who it is. Seeded, so the same world and time give the same visitor.
 */
export function idleVisitorAt(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): { k: number; until: number; visitor: string } | undefined {
  if (!idleBonusOn(content, 'visitor')) return undefined;
  const v = content.bonuses.visitors;
  const slot = idleOpenSlot(
    state,
    LUCK_VISITOR,
    { every: v.everyMs, jitter: v.jitterMs, lasts: v.lastsMs },
    now,
  );
  if (!slot) return undefined;
  const r = idleChance(idleSeed(state), LUCK_VISITOR_WHO, slot.k);
  return { ...slot, visitor: idleVisitorKind(v.kinds, r) };
}

/**
 * D313: visitors who called while the player was here, were not met, and had gone by the time
 * the player stopped counting as here, so they called while the player was away. Who called, in
 * the order they came. The same window as `idleMissedFindSlots` (D254), for visitors.
 */
export function idleMissedVisitors(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): string[] {
  if (!idleBonusOn(content, 'visitor')) return [];
  const v = content.bonuses.visitors;
  const slot = { every: v.everyMs, jitter: v.jitterMs, lasts: v.lastsMs };
  const seen = state.lastSeen;
  const present = Math.min(now, seen + content.bonuses.presentMs);
  const claimed = state.claimed ?? [];
  const from = Math.max(
    1,
    Math.floor((seen - slot.lasts - slot.jitter - state.createdAt) / slot.every),
  );
  const to = Math.floor((present + slot.jitter - state.createdAt) / slot.every) + 1;
  const left = seen + content.bonuses.presentMs;
  const out: string[] = [];
  for (let j = from; j <= to; j++) {
    const opens = idleSlotOpens(state, LUCK_VISITOR, slot, j);
    const goes = opens + slot.lasts;
    if (opens < present && goes > left && goes <= now && !claimed.includes(`visitor:${j}`)) {
      const r = idleChance(idleSeed(state), LUCK_VISITOR_WHO, j);
      out.push(idleVisitorKind(v.kinds, r));
    }
  }
  return out;
}

/** D289: what a visitor pays when tapped: a few seconds of the world's income, tapped bars counted. */
export function idleVisitorCoins(state: IdleWorldState, content: IdleContent, now: number): number {
  const worth = content.bonuses.visitors.worthSeconds;
  return Math.max(1, Math.floor(idleIncome(state, content, now, true) * worth));
}

/**
 * D370: the year has ended (a festival held, or the world passed on), so every timed bonus ends
 * and every offer open at `now` is withdrawn: marked claimed, so it never shows in the new year.
 */
export function idleEndTimed(
  state: IdleWorldState,
  content: IdleContent,
  now: number,
): IdleWorldState {
  const { festivalPending: _pending, ...open } = state;
  const ids = idleBonusOffers({ ...open, lastSeen: now }, content, now).map((o) => o.id);
  const claimed = [...(state.claimed ?? []), ...ids];
  return { ...state, bonuses: [], ...(ids.length > 0 || state.claimed ? { claimed } : {}) };
}

/** Claim an offer: start a market day, or pick up a lucky find. */
export function claimIdleBonus(
  state: IdleWorldState,
  content: IdleContent,
  id: string,
  now: number,
): IdleResult {
  const offer = idleBonusOffers(state, content, now).find((o) => o.id === id);
  if (!offer) return { ok: false, reason: 'no_offer' };
  const s = checkInIdle(state, content, now);
  // Keep only claims whose slot could still be open, so the list stays short.
  const keep = (s.claimed ?? []).filter(
    (c) => !c.startsWith(offer.kind) || Number(c.split(':')[1]) >= Number(id.split(':')[1]) - 1,
  );
  const claimed = { ...s, claimed: [...keep, id] };
  if (offer.kind === 'market_day') {
    const m = content.bonuses.marketDay;
    const boost = { source: 'market_day', stat: 'value' as const, factor: m.factor };
    const bonuses = [...claimed.bonuses, { ...boost, from: now, until: now + m.durationMs }];
    return { ok: true, state: normaliseIdle({ ...claimed, bonuses }, content, now) };
  }
  const coins =
    offer.kind === 'visitor'
      ? idleVisitorCoins(s, content, now)
      : idleLuckyFindCoins(s, content, now);
  // Earned while here, so `earnedAtSeen` moves too and it never counts as away earnings.
  // D331: and Trimmings, tapped only.
  const t = content.trimmings;
  const trimmed = idleEarnTrimmings(claimed, offer.kind === 'visitor' ? t.visitor : t.luckyFind);
  const y = trimmed.year;
  const p = trimmed.permanent;
  const paid: IdleWorldState = {
    ...trimmed,
    // D316: held below IDLE_MAX, never Infinity.
    year: { ...y, coins: idleClamp(y.coins + coins), earned: idleClamp(y.earned + coins) },
    permanent: { ...p, lifetimeEarned: idleClamp(p.lifetimeEarned + coins) },
    earnedAtSeen: idleClamp(trimmed.earnedAtSeen + coins),
  };
  // D290: a visitor met goes in the collection. D325: and adds a little to the Lineage meter.
  const met = offer.kind === 'visitor' ? idleFillLineage(paid, content, 'visitor') : paid;
  return { ok: true, state: noteIdleCollection(met, content, now, offer.visitor) };
}
